import { z } from 'zod';
import { addDays } from 'date-fns';
import { parseDateOnlyLocal } from '@erp/shared-utils';
import type { DateOnlyRange } from 'src/components/common/date-pickers/dateRangePresets';
import {
  AGENT_RUN_CATEGORIES,
  AgentRunCategorySchema,
  type AgentRunCategory,
  type AgentRunListFilter,
} from '@erp/rpc';

/**
 * What the agent page's History list shows, and which run is open, and how
 * that rides in the URL so a link reproduces the view
 * (`/agent-pages/<id>?run=<uuid>&exclude=skipped,cancelled`). Pure — no
 * React, no router — so the precedence rules stay unit-testable.
 *
 * Shaped like the `agentDefinitions.listRuns` filter on purpose: a later
 * filter is a new field here, a new search param, and a new field on the
 * request, without reshaping the props that carry this object down the page.
 */
export interface RunHistoryFilter {
  /**
   * Run categories left out (see `AgentRunCategorySchema`), always in
   * {@link AGENT_RUN_CATEGORIES} order with no repeats, so equal filters
   * compare and cache as equal.
   */
  excludeCategories: AgentRunCategory[];
  /**
   * Calendar days the runs started on, inclusive on both sides, in the
   * viewer's own time zone; either side may be open. `{}` for no range.
   */
  range: DateOnlyRange;
}

/** A filter leaving out `categories`, in canonical order, over `range`. */
export function excludingCategories(
  categories: Iterable<AgentRunCategory>,
  range: DateOnlyRange = {},
): RunHistoryFilter {
  const excluded = new Set(categories);
  return {
    excludeCategories: AGENT_RUN_CATEGORIES.filter((c) => excluded.has(c)),
    range: normalizeRange(range),
  };
}

/**
 * A range with its sides swapped when they are backwards (a hand-edited
 * link), so it still means "between these two days".
 */
function normalizeRange({ from, to }: DateOnlyRange): DateOnlyRange {
  if (from && to && from > to) return { from: to, to: from };
  return { ...(from && { from }), ...(to && { to }) };
}

/** The filter over a new date range, categories kept. */
export function withRange(
  filter: RunHistoryFilter,
  range: DateOnlyRange,
): RunHistoryFilter {
  return excludingCategories(filter.excludeCategories, range);
}

/** Skipped checks are noise for a scheduled agent, so the list starts without them. */
export const DEFAULT_RUN_HISTORY_FILTER: RunHistoryFilter = excludingCategories(
  ['skipped'],
);

/** Whether the filter leaves out skipped checks (the "Hide skipped" switch). */
export function hidesSkipped(filter: RunHistoryFilter): boolean {
  return filter.excludeCategories.includes('skipped');
}

/** The filter with skipped checks hidden or shown, everything else kept. */
export function withSkippedHidden(
  filter: RunHistoryFilter,
  hide: boolean,
): RunHistoryFilter {
  return withCategoryShown(filter, 'skipped', !hide);
}

/** The filter with one category shown or left out, everything else kept. */
export function withCategoryShown(
  filter: RunHistoryFilter,
  category: AgentRunCategory,
  shown: boolean,
): RunHistoryFilter {
  const excluded = new Set(filter.excludeCategories);
  if (shown) excluded.delete(category);
  else excluded.add(category);
  return excludingCategories(excluded, filter.range);
}

/** Whether the filter leaves out skipped checks and nothing else. */
export function hidesOnlySkipped(filter: RunHistoryFilter): boolean {
  return (
    filter.excludeCategories.length === 1 &&
    filter.excludeCategories[0] === 'skipped'
  );
}

/** The `exclude` value meaning "leave nothing out". */
const EXCLUDE_NONE = 'none';

/**
 * `validateSearch` for the agent page route. A malformed value (a hand-edited
 * link) is dropped rather than failing the route.
 *
 * `exclude` is a comma list of categories, or `none` for "show everything";
 * absent means "use the saved choice or the default". `skipped` is the older
 * AI-529 form, still read so earlier links keep working.
 */
/**
 * Years a URL day may name. Outside them the instant sent to the server is
 * out of range: year 9999 + 1 day serializes as `+010000-…`, which the API
 * rejects, and year 0001 can shift into year 0 in a zone east of UTC.
 */
const MIN_URL_YEAR = 1900;
const MAX_URL_YEAR = 9998;

const dateOnlyParam = z
  .string()
  .refine((value) => {
    const day = parseDateOnlyLocal(value);
    if (!day) return false;
    const year = day.getFullYear();
    return year >= MIN_URL_YEAR && year <= MAX_URL_YEAR;
  })
  .optional()
  .catch(undefined);

export const agentPageSearchSchema = z.object({
  exclude: z.string().optional().catch(undefined),
  skipped: z.enum(['show', 'hide']).optional().catch(undefined),
  /** First day of the date range (`YYYY-MM-DD`, the viewer's time zone). */
  from: dateOnlyParam,
  /** Last day of the date range, inclusive. */
  to: dateOnlyParam,
  /**
   * The run the page opens on. Absent means "whatever is running, else the
   * newest" — a shared link names the run so a ticket does not land on a
   * later one.
   */
  run: z.uuid().optional().catch(undefined),
});
export type AgentPageSearch = z.infer<typeof agentPageSearchSchema>;

/**
 * The categories an `exclude` param names, or null when it names none (an
 * empty or garbled value: fall back as if absent). Unknown names are dropped
 * so a link from a newer version still opens.
 */
export function parseExcludeParam(
  raw: string | undefined,
): RunHistoryFilter | null {
  if (raw === undefined) return null;
  if (raw.trim() === EXCLUDE_NONE) return excludingCategories([]);
  const known = raw
    .split(',')
    .map((part) => AgentRunCategorySchema.safeParse(part.trim()))
    .flatMap((parsed) => (parsed.success ? [parsed.data] : []));
  return known.length > 0 ? excludingCategories(known) : null;
}

/**
 * The filter in effect: the URL wins (it is what a shared link means), then
 * the viewer's last choice on this browser, then the default.
 */
export function resolveRunHistoryFilter(
  search: AgentPageSearch,
  saved: RunHistoryFilter | null,
): RunHistoryFilter {
  // The range only ever comes from the URL: a remembered week would
  // silently narrow the list the next time the viewer opens any agent.
  const range = { from: search.from, to: search.to };
  return withRange(resolveCategories(search, saved), range);
}

function resolveCategories(
  search: AgentPageSearch,
  saved: RunHistoryFilter | null,
): RunHistoryFilter {
  const fromUrl = parseExcludeParam(search.exclude);
  if (fromUrl) return fromUrl;
  if (search.skipped === 'show') return excludingCategories([]);
  if (search.skipped === 'hide') return DEFAULT_RUN_HISTORY_FILTER;
  return saved ?? DEFAULT_RUN_HISTORY_FILTER;
}

/**
 * The search params that pin `filter`, so the URL can be shared as-is. Drops
 * the older `skipped` param: `exclude` now says it all.
 */
export function runHistorySearch(filter: RunHistoryFilter): {
  exclude: string;
  skipped: undefined;
  from: string | undefined;
  to: string | undefined;
} {
  return {
    exclude:
      filter.excludeCategories.length > 0
        ? filter.excludeCategories.join(',')
        : EXCLUDE_NONE,
    skipped: undefined,
    from: filter.range.from,
    to: filter.range.to,
  };
}

/** Local midnight at the start of a `YYYY-MM-DD` day, or null if invalid. */
function startOfLocalDay(day: string | undefined): Date | null {
  return day ? parseDateOnlyLocal(day) : null;
}

/**
 * The range as the instants `listRuns` takes: from the start of the first
 * day up to (not including) the start of the day after the last, both at the
 * viewer's local midnight — so "Oct 4 to Oct 10" is the whole of both days.
 */
function rangeInstants(range: DateOnlyRange): {
  startedFrom?: string;
  startedTo?: string;
} {
  const from = startOfLocalDay(range.from);
  const to = startOfLocalDay(range.to);
  return {
    ...(from && { startedFrom: from.toISOString() }),
    ...(to && { startedTo: addDays(to, 1).toISOString() }),
  };
}

/**
 * Search params that open this page on `runId`, keeping the history filter
 * already in the URL.
 */
export function searchForRun(
  search: AgentPageSearch,
  runId: string,
): AgentPageSearch {
  return { ...search, run: runId };
}

/**
 * The request filter for `agentDefinitions.listRuns`. Also sends AI-529's
 * `hideSkipped` when skipped checks are left out: a backend from before
 * `excludeCategories` silently drops that field, and would otherwise list
 * the skipped checks the viewer asked to hide (a deploy that ships the
 * frontend first). Current backends treat the two as one exclusion set.
 */
export function toListRunsFilter(filter: RunHistoryFilter): AgentRunListFilter {
  return {
    excludeCategories: filter.excludeCategories,
    ...(hidesSkipped(filter) && { hideSkipped: true }),
    ...rangeInstants(filter.range),
  };
}

/**
 * Whether the range has fully ended by `now`, so no new run can land in it
 * and the list has no reason to poll.
 */
export function rangeIsInThePast(filter: RunHistoryFilter, now: Date): boolean {
  const end = rangeInstants(filter.range).startedTo;
  return end !== undefined && new Date(end).getTime() <= now.getTime();
}

/** A stable string per filter, to tell when the filter changed. */
export function runHistoryFilterKey(filter: RunHistoryFilter): string {
  const categories =
    filter.excludeCategories.length > 0
      ? `exclude:${filter.excludeCategories.join(',')}`
      : 'all';
  return `${categories}|${filter.range.from ?? ''}..${filter.range.to ?? ''}`;
}

/**
 * How many History rows to ask for: the grown window while it still belongs
 * to the current filter, else the first page of the new list.
 */
export function historyLimitFor(
  paging: { filterKey: string; limit: number },
  currentFilterKey: string,
  pageSize: number,
): number {
  return paging.filterKey === currentFilterKey ? paging.limit : pageSize;
}

/** Browser-storage key for the viewer's last choice (one for all agent pages). */
export const RUN_HISTORY_FILTER_STORAGE_KEY = 'agent-page:run-history-filter';

/**
 * The stored choice: today's shape, or AI-529's `{ hideSkipped }`. Unknown
 * category names (from a newer version) are dropped.
 */
const savedFilterSchema = z.union([
  z
    .object({ excludeCategories: z.array(z.string()) })
    .transform(({ excludeCategories }) =>
      excludingCategories(
        excludeCategories.flatMap((name) => {
          const parsed = AgentRunCategorySchema.safeParse(name);
          return parsed.success ? [parsed.data] : [];
        }),
      ),
    ),
  z
    .object({ hideSkipped: z.boolean() })
    .transform(({ hideSkipped }) =>
      excludingCategories(hideSkipped ? ['skipped'] : []),
    ),
]);

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

/**
 * The viewer's last choice, or null when there is none or it cannot be read
 * (private mode, blocked storage, a value from an older shape).
 */
export function readSavedRunHistoryFilter(
  storage: ReadableStorage | null,
): RunHistoryFilter | null {
  try {
    const raw = storage?.getItem(RUN_HISTORY_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = savedFilterSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Remember the viewer's run-type choice (not the date range). Best-effort:
 * storage may be unavailable.
 */
export function saveRunHistoryFilter(
  storage: WritableStorage | null,
  filter: RunHistoryFilter,
): void {
  try {
    storage?.setItem(
      RUN_HISTORY_FILTER_STORAGE_KEY,
      JSON.stringify({ excludeCategories: filter.excludeCategories }),
    );
  } catch {
    // Quota, private mode or blocked storage: the URL still carries the view.
  }
}

/** `window.localStorage`, or null where touching it throws. */
export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
